import { Link } from "wouter";
import { Wordmark } from "@workspace/brand-react";
import { Globe2 } from "lucide-react";
import { INDUSTRIES } from "@/lib/industries";

export function Footer() {
  return (
    <footer className="border-t border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))] mt-24">
      <div className="mx-auto max-w-6xl px-6 py-14 grid sm:grid-cols-2 gap-10 md:grid-cols-12">
        <div className="sm:col-span-2 md:col-span-4">
          <Wordmark size="md" />
          <p className="mt-4 text-sm text-[hsl(var(--brand-slate))] max-w-sm">
            Identity-verified change requests for any business that captures sensitive details from clients. Built in Australia for trust, safety and audit-readiness.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[hsl(var(--brand-sand))] bg-white/60 px-3 py-1.5 text-[11px] font-mono tracking-[0.16em] uppercase text-[hsl(var(--brand-slate))]" data-testid="badge-footer-au">
            <Globe2 className="w-3.5 h-3.5 text-[hsl(var(--brand-forest))]" />
            Hosted in Australia
          </div>
          <p className="mt-4 text-[11px] text-[hsl(var(--brand-slate))] leading-relaxed max-w-sm">
            <a
              href="https://connectid.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[hsl(var(--brand-forest))]"
            >
              ConnectID®
            </a>
            {" "}is a registered trade mark of ConnectID Pty Ltd ABN 80 648 970 101. ConnectID® is part of{" "}
            <a
              href="https://auspayplus.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[hsl(var(--brand-forest))]"
            >
              AP+
            </a>
            {" "}(Australian Payments Plus). AP+ operates Australia's domestic payments and identity infrastructure, bringing together trusted Australian brands with 40 years' experience including BPAY, eftpos, NPP, Osko, PayID and PayTo.
          </p>
        </div>
        <FooterCol title="Product" className="md:col-span-2">
          <FooterLink to="/how-it-works">How it works</FooterLink>
          <FooterLink to="/pricing">Pricing</FooterLink>
          <FooterLink to="/security">Security</FooterLink>
          <FooterExt to="/app/sign-up">Get started</FooterExt>
        </FooterCol>
        <FooterCol title="Industries" className="md:col-span-2">
          {INDUSTRIES.map((i) => (
            <FooterLink key={i.slug} to={`/industries/${i.slug}`}>{i.name}</FooterLink>
          ))}
          <FooterLink to="/industries">All industries</FooterLink>
        </FooterCol>
        <FooterCol title="Company" className="md:col-span-2">
          <FooterLink to="/about">About</FooterLink>
          <FooterLink to="/contact">Contact</FooterLink>
          <FooterLink to="/legal/terms">Terms</FooterLink>
          <FooterLink to="/legal/privacy">Privacy</FooterLink>
          <FooterLink to="/legal/dpa">DPA</FooterLink>
          <FooterLink to="/legal/connectid-terms">ConnectID® Terms</FooterLink>
        </FooterCol>
        <FooterCol title="Get in touch" className="md:col-span-2">
          <FooterLink to="/contact?topic=sales">Sales</FooterLink>
          <FooterLink to="/contact?topic=support">Support</FooterLink>
          <FooterLink to="/contact?topic=security">Security disclosures</FooterLink>
        </FooterCol>
      </div>
      <div className="border-t border-[hsl(var(--brand-sand))]">
        <div className="mx-auto max-w-6xl px-6 py-6 text-xs text-[hsl(var(--brand-slate))]">
          © {new Date().getFullYear()} FortifyID. Made in Australia.
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, className, children }: { title: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--brand-slate))] mb-3">{title}</div>
      <ul className="space-y-2 text-sm">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link href={to} className="text-[hsl(var(--brand-charcoal))] hover:text-[hsl(var(--brand-forest))]">
        {children}
      </Link>
    </li>
  );
}

function FooterExt({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <a href={to} className="text-[hsl(var(--brand-charcoal))] hover:text-[hsl(var(--brand-forest))]">
        {children}
      </a>
    </li>
  );
}
